import { useEffect, useState } from "react";
import type { FC } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { Box, Drawer, useTheme } from "@mui/material";
import NavSection from "../NavSection";
import Scrollbar from "../Scrollbar";
import { useTranslation } from "react-i18next";
import { useStyles } from "../../theme/styles";
import { tenantSettingsService } from "src/services/tenantSettings";
import {
	Assignment as AssignmentIcon,
	ArticleOutlined as ArticleOutlinedIcon,
} from "@mui/icons-material";
import { MenuRoutes } from "src/types/routes";
import {
	DashboardTranslationKeys,
	HTTPSTATUSCODES,
	Roles,
} from "src/utils/constants";
import { getCurrentUserRoles } from "src/utils/jwt-helper";
import { Permissions } from "src/types/settings";
import {
	MailTemplate24Regular,
	PeopleListFilled,
	PlugConnected24Filled,
	PlugConnected24Regular,
	Tag24Regular,
	TextBulletListSquare24Filled,
	WrenchScrewdriver24Regular,
} from "@fluentui/react-icons";
import { StorageKeys } from "src/utils/storageKeys";
import Lockr from "lockr";
import AiIndicator from "../shared/AiIndicator";

interface DashboardSidebarProps {
	onMobileClose: () => void;
	openMobile: boolean;
}

const COLOR = "rgba(192,213,220,255)";

const IconsStyle = {
	width: "18px",
	height: "20px",
	marginRight: "8px",
	color: COLOR,
};

const SkillsIconStyle = {
	width: "28px",
	marginLeft: "-5px",
	marginRight: "3px",
	color: COLOR,
};

const TemplatesIconStyle = {
	marginLeft: "-2px",
	marginRight: "3px",
	color: COLOR,
};

const KnowledgeIconStyle = {
	width: "24px",
	marginLeft: "-2px",
	marginRight: "3px",
	color: COLOR,
};

const McpIconStyle = {
	marginLeft: "-2px",
	marginRight: "3px",
	color: COLOR,
};

const sidebarWidth = 320;

const DashboardSidebar: FC<DashboardSidebarProps> = (props) => {
	const classes = useStyles();
	const { onMobileClose, openMobile } = props;
	const location = useLocation();
	const { t } = useTranslation();
	const [selectedMenuPath, setSelectedMenuPath] = useState<string>(
		location.pathname
	);
	const [permissions, setPermissions] = useState<Permissions>();
	const theme = useTheme();

	const getPermissions = async () => {
		try {
			const response = await tenantSettingsService.getPermissions();

			if (response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {
				setPermissions(response.data);
				Lockr.set(StorageKeys.Permissions, response.data);
			}
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		getPermissions().catch(console.error);
	}, []);

  const settingsRoutes = [
    MenuRoutes.GeneralSettings,
    MenuRoutes.Schedules,
    MenuRoutes.Announcements,
    MenuRoutes.Knowledge,
    MenuRoutes.McpServers,
    MenuRoutes.Events,
    MenuRoutes.WrapUp,
    MenuRoutes.ApiDocumentation,
    MenuRoutes.DataRetentions,
    MenuRoutes.Tags
  ];

	const userManagementRoutes = [MenuRoutes.Users, MenuRoutes.AiAgents, MenuRoutes.InsightsUsers];

	const isSettingsOpen = () =>
		settingsRoutes.reduce(
			(finalVal, currentVal) =>
				finalVal || selectedMenuPath.includes(currentVal),
			false
		);

	const mainMenuSections = [
		{
			title: "",
			roles: [
				Roles.WebAdmin,
				Roles.Supervisor,
				Roles.AgentPlus,
				Roles.CC4AllAdmin,
			],
			items: [
				{
					title: t("DashBoardSidebar.Dashboard"),
					path: MenuRoutes.Dashboard,
					icon: (
						<img
							alt="DashboardIcon"
							src="/static/menu/DashboardMenuIcon.svg"
							style={IconsStyle}
						/>
					),
				},
			],
			onClick: () => setSelectedMenuPath(MenuRoutes.Dashboard),
		},
		{
			title: "",
			roles: [
				Roles.WebAdmin,
				Roles.Supervisor,
				Roles.AgentPlus,
				Roles.CC4AllAdmin,
			],
			items: [
				{
					title: t("DashBoardSidebar.Calls"),
					path: MenuRoutes.Calls,
					testId: "nav-item-calls",
					icon: (
						<img
							alt="TelephonyIcon"
							src="/static/menu/CallsMenuIcon.svg"
							style={IconsStyle}
						/>
					),
					children: [
						{
							title: t(DashboardTranslationKeys.Queues),
							path: `${MenuRoutes.Calls}/${MenuRoutes.Queues}`,
							testId: "nav-item-queues",
							roles: [
								Roles.WebAdmin,
								Roles.Supervisor,
								Roles.AgentPlus,
								Roles.CC4AllAdmin,
							],
						},
						{
							title: t("DashBoardSidebar.Flows"),
							path: `${MenuRoutes.Calls}/${MenuRoutes.Flows}`,
							testId: "nav-item-flows",
							roles: [Roles.WebAdmin],
						},
						{
							title: t("DashBoardSidebar.ResourceAccounts"),
							path: `${MenuRoutes.Calls}/${MenuRoutes.EntryPoints}`,
							testId: "nav-item-resource-accounts",
							roles: [Roles.WebAdmin],
						},
					],
				},
			],
			open: selectedMenuPath.includes(MenuRoutes.Calls),
			onClick: () => setSelectedMenuPath(MenuRoutes.Calls),
		},
		{
			title: "",
			roles: [
				Roles.WebAdmin,
				Roles.Supervisor,
				Roles.AgentPlus,
				Roles.CC4AllAdmin,
			],
			items: [
				{
					title: t("DashBoardSidebar.Generic"),
					path: MenuRoutes.Generic,
					testId: "nav-item-generic",
					icon: (
						<AssignmentIcon
							style={{ ...IconsStyle }}
							fontSize="large"
						/>
					),
					children: [
						{
							title: t(DashboardTranslationKeys.Queues),
							path: `${MenuRoutes.Generic}/${MenuRoutes.Queues}`,
							roles: [
								Roles.WebAdmin,
								Roles.Supervisor,
								Roles.AgentPlus,
								Roles.CC4AllAdmin,
							],
						},
						{
							title: t("DashBoardSidebar.EntryPoints"),
							path: `${MenuRoutes.Generic}/${MenuRoutes.EntryPoints}`,
							testId: "nav-item-entry-points",
							roles: [Roles.WebAdmin],
						},
					],
				},
			],
			open: selectedMenuPath.includes(MenuRoutes.Generic),
			display: permissions?.genericChannelEnabled ? "block" : "none",
			onClick: () => setSelectedMenuPath(MenuRoutes.Generic),
		},
		{
			title: "",
			roles: [
				Roles.WebAdmin,
				Roles.Supervisor,
				Roles.AgentPlus,
				Roles.CC4AllAdmin,
			],
			items: [
				{
					title: t("DashBoardSidebar.Text"),
					path: MenuRoutes.Text,
					testId: "nav-item-text",
					icon: (
						<ArticleOutlinedIcon
							style={{ ...IconsStyle }}
							fontSize="large"
						/>
					),
					children: [
						{
							title: t(DashboardTranslationKeys.Queues),
							path: `${MenuRoutes.Text}/${MenuRoutes.Queues}`,
							testId: "nav-item-queues",
							roles: [
								Roles.WebAdmin,
								Roles.Supervisor,
								Roles.AgentPlus,
								Roles.CC4AllAdmin,
							],
						},
						{
							title: t("DashBoardSidebar.Flows"),
							path: `${MenuRoutes.Text}/${MenuRoutes.Flows}`,
							testId: "nav-item-flows",
							roles: [Roles.WebAdmin],
							sx: {
								display: permissions?.textFlowsEnabled
									? "block"
									: "none",
							},
						},
						{
							title: t("DashBoardSidebar.ResourceAccounts"),
							path: `${MenuRoutes.Text}/${MenuRoutes.EntryPoints}`,
							testId: "nav-item-resource-accounts",
							roles: [Roles.WebAdmin],
						},
					],
				},
			],
			open: selectedMenuPath.includes(MenuRoutes.Text),
			display: permissions?.textChannelEnabled ? "block" : "none",
			onClick: () => setSelectedMenuPath(MenuRoutes.Text),
		},
	];

	const getIcon = (iconName: string) => {
		const style =
			iconName === "SkillsMenuIcon"
				? SkillsIconStyle
				: iconName === "AiAgentsMenuIcon"
				? KnowledgeIconStyle
				: IconsStyle;
		return (
			<img
				alt={iconName}
				src={`/static/menu/${iconName}.svg`}
				style={style}
			/>
		);
	};

	const secondaryMenuSections = [
		{
			title: "",
			roles: [Roles.WebAdmin],
			items: [
				{
					title: t("DashBoardSidebar.UserManagement"),
					path: MenuRoutes.UserManagement,
					testId: "nav-item-user-management",
					icon: getIcon("UserManagementMenuIcon"),
					roles: [Roles.WebAdmin, Roles.Supervisor],
					children: [
						{
							title: t("DashBoardSidebar.Users"),
							path: MenuRoutes.Users,
							testId: "nav-item-users",
							icon: getIcon("AgentsMenuIcon"),
							roles: [Roles.WebAdmin],
						},
						...(permissions?.hasInsightsLicense
							? [
									{
										title: t("DashBoardSidebar.InsightsUsers"),
										path: MenuRoutes.InsightsUsers,
										testId: "nav-item-insights-users",
										icon: <PeopleListFilled style={{ ...IconsStyle, color: location.pathname === MenuRoutes.InsightsUsers ? theme.palette.primary.main : COLOR }} />,
										roles: [Roles.WebAdmin],
									},
							  ]
							: []),
						...(permissions?.aiAgentsEnabled
							? [
									{
										title: t("DashBoardSidebar.AiAgents"),
										path: MenuRoutes.AiAgents,
										testId: "nav-item-ai-agents",
										icon: getIcon("AiAgentsMenuIcon"),
										roles: [Roles.WebAdmin],
									},
							  ]
							: []),
					],
				},
			],
			open: selectedMenuPath.includes(MenuRoutes.UserManagement),
			onClick: () => setSelectedMenuPath(userManagementRoutes.join(",")),
		},
		{
			title: "",
			roles: [Roles.WebAdmin, Roles.Supervisor],
			items: [
				{
					title: t("DashBoardSidebar.Recordings"),
					path: MenuRoutes.Recordings,
					testId: "nav-item-recordings",
					icon: getIcon("RecordingsMenuIcon"),
				},
			],
			display: permissions?.recordingsEnabled ? "block" : "none",
			onClick: () => setSelectedMenuPath(MenuRoutes.Recordings),
		},
		{
			title: "",
			items: [
				{
					title: t("DashBoardSidebar.Settings"),
					testId: "nav-item-settings",
					icon: getIcon("SettingsMenuIcon"),
					roles: [Roles.WebAdmin, Roles.Supervisor],
					children: [
						{
							title: t("DashBoardSidebar.General"),
							path: MenuRoutes.GeneralSettings,
							testId: "nav-item-general",
							icon: getIcon("SettingsMenuIcon"),
							roles: [Roles.WebAdmin],
						},
						{
							title: t("DashBoardSidebar.ImportContacts"),
							path: MenuRoutes.Contacts,
							testId: "nav-item-import-contacts",
							icon: getIcon("ContactImportIcon"),
							roles: [Roles.WebAdmin, Roles.Supervisor],
						},
						{
							title: t("DashBoardSidebar.Skills"),
							path: MenuRoutes.Skills,
							testId: "nav-item-skills",
							icon: getIcon("SkillsMenuIcon"),
							roles: [Roles.WebAdmin],
							sx: {
								display: permissions?.skillsEnabled
									? "block"
									: "none",
							},
						},
						{
							title: t("DashBoardSidebar.Tags"),
							path: MenuRoutes.Tags,
							testId: "nav-item-tags",
							icon: <Tag24Regular style={TemplatesIconStyle} />,
							roles: [Roles.WebAdmin],
						},
						{
							title: t("DashBoardSidebar.Announcements"),
							path: MenuRoutes.Announcements,
							testId: "nav-item-media-files",
							icon: getIcon("MediaFilesMenuIcon"),
							roles: [Roles.WebAdmin],
						},
						{
							title: t("DashBoardSidebar.Knowledge"),
							path: MenuRoutes.Knowledge,
							testId: "nav-item-knowledge",
							icon: getIcon("AiAgentsMenuIcon"),
							roles: [Roles.WebAdmin],
						},
						{
							title: t("DashBoardSidebar.McpServers"),
							path: MenuRoutes.McpServers,
							testId: "nav-item-mcp-servers",
							icon: (
								<WrenchScrewdriver24Regular
									style={McpIconStyle}
								/>
							),
							roles: [Roles.WebAdmin],
						},
						{
							title: t("DashBoardSidebar.Templates"),
							path: MenuRoutes.Templates,
							testId: "nav-item-templates",
							icon: (
								<MailTemplate24Regular
									style={TemplatesIconStyle}
								/>
							),
							roles: [Roles.WebAdmin],
							sx: {
								display: permissions?.textChannelEnabled
									? "block"
									: "none",
							},
						},
						{
							title: t("DashBoardSidebar.AIPrompts"),
							path: MenuRoutes.AIPrompts,
							testId: "nav-item-ai-prompts",
							icon: (
								<MailTemplate24Regular
									style={TemplatesIconStyle}
								/>
							),
							info: (
								<AiIndicator mode="icon-only" />
							),
							roles: [Roles.WebAdmin],
							sx: {
								display: permissions?.textChannelEnabled
									? "block"
									: "none",
							},
						},
						{
							title: t("DashBoardSidebar.Schedule"),
							path: MenuRoutes.Schedules,
							testId: "nav-item-schedule",
							icon: getIcon("ScheduleMenuIcon"),
							roles: [Roles.WebAdmin],
						},
						{
							title: t("DashBoardSidebar.Events"),
							path: MenuRoutes.Events,
							testId: "nav-item-events",
							icon: getIcon("EventMenuIcon"),
							roles: [Roles.WebAdmin, Roles.Supervisor],
						},
						{
							title: t("DashBoardSidebar.WrapUp"),
							path: MenuRoutes.WrapUp,
							testId: "nav-item-wrap-up",
							icon: getIcon("WrapUpMenuIcon"),
							roles: [Roles.WebAdmin],
						},
						{
							title: t("DashBoardSidebar.ApiDocumentation"),
							path: MenuRoutes.ApiDocumentation,
							testId: "nav-item-api-documentation",
							icon: getIcon("ApiDocumentationMenuIcon"),
							roles: [Roles.WebAdmin, Roles.Supervisor],
						},
						{
							title: t("DashBoardSidebar.DataRetentions"),
							path: MenuRoutes.DataRetentions,
							testId: "nav-item-data-retentions",
							icon: getIcon("DataRetentionSettingsIcon"),
							roles: [Roles.WebAdmin, Roles.Supervisor],
						},
					],
				},
			],
			roles: [Roles.WebAdmin, Roles.Supervisor],
			open: isSettingsOpen(),
			onClick: () => setSelectedMenuPath(settingsRoutes.join(",")),
		},
		{
			title: "",
			roles: [Roles.WebAdmin],
			items: [
				{
					title: t("DashBoardSidebar.Setup"),
					path: MenuRoutes.Setup,
					testId: "nav-item-setup",
					icon: getIcon("SetupMenuIcon"),
					children: [
						{
							title: t("DashBoardSidebar.SetupGeneral"),
							path: MenuRoutes.Setup,
							testId: "nav-item-setup-general",
							icon: getIcon("SettingsMenuIcon"),
							roles: [Roles.WebAdmin],
						},
						...(permissions?.salesforceEnabled || permissions?.dynamicsEnabled || permissions?.hubSpotEnabled
							? [
									{
										title: t("DashBoardSidebar.Integrations"),
										path: MenuRoutes.Integrations,
										testId: "nav-item-integrations",
										icon:
											location.pathname === MenuRoutes.Integrations ? (
												// Active: solid (bold) variant recolored to the primary accent,
												// consistent with the other sidebar items whose icons recolor on
												// active (NavItem's `& img` filter; the Insights > Users icon uses
												// primary.main the same way).
												<PlugConnected24Filled
													style={{
														...TemplatesIconStyle,
														color: theme.palette.primary.main,
													}}
												/>
											) : (
												<PlugConnected24Regular
													style={TemplatesIconStyle}
												/>
											),
										roles: [Roles.WebAdmin],
									},
								]
							: []),
					],
				},
			],
			open: selectedMenuPath.includes(MenuRoutes.Setup),
			onClick: () => setSelectedMenuPath(MenuRoutes.Setup),
		},
		{
			title: "",
			roles: [Roles.WebAdmin],
			items: [
				{
					title: t("DashBoardSidebar.Subscriptions"),
					path: MenuRoutes.Subscriptions,
					testId: "nav-item-subscriptions",
					icon: (
						<TextBulletListSquare24Filled
							style={TemplatesIconStyle}
						/>
					),
				},
			],
			display: "block",
			onClick: () => setSelectedMenuPath(MenuRoutes.Subscriptions),
		},	
	];

	useEffect(() => {
		if (openMobile && onMobileClose) {
			onMobileClose();
		}
	}, [location.pathname]);

	const returnSection = (section: any) => {
		return (
			<NavSection
				key={section.items[0].title}
				pathname={location.pathname}
				sx={{
					"& + &": {
						mt: -2,
					},
					display: section.display ?? "block",
					color: theme.palette.text.primary,
				}}
				{...section}
			/>
		);
	};

	const filteredMainMenuSections = mainMenuSections
		.filter((m) => m.roles.some((r) => getCurrentUserRoles().includes(r)))
		.map((section) => returnSection(section));

	const content = (
		<Scrollbar
			options={{ suppressScrollX: true, suppressScrollY: false }}
			style={{ backgroundColor: theme.palette.background.paper }}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "100%",
					justifyContent: "space-between",
				}}
			>
				<div>
					<Box
						sx={{
							p: 4,
							textAlign: "center",
							display: "flex",
							flexDirection: "row",
							justifyContent: "center",
						}}
					>
						<img
							src={
								theme.palette.mode === "light"
									? "/static/CC4TeamsLogo.png"
									: "/static/CC4TeamsLogoWhite.png"
							}
							alt="cc4allLogo"
							height="100px"
							className={classes.logoImg}
						/>
					</Box>

					<Box className={classes.mainNavSection}>
						{filteredMainMenuSections}
					</Box>
				</div>

				<Box className={classes.secondaryNavSection}>
					{secondaryMenuSections
						.filter((m) =>
							m.roles.some((r) =>
								getCurrentUserRoles().includes(r)
							)
						)
						.map((section) => returnSection(section))}
				</Box>
			</Box>
		</Scrollbar>
	);

	return (
		<>
			<Drawer
				anchor="left"
				onClose={onMobileClose}
				open={openMobile}
				data-testid="dashboard-sidebar-mobile"
				PaperProps={{
					sx: {
						backgroundColor: theme.palette.background.paper,
						width: sidebarWidth,
						display: { lg: "none", xs: "block" },
					},
				}}
				variant="temporary"
			>
				{content}
			</Drawer>

			<Drawer
				anchor="left"
				open
				data-testid="dashboard-sidebar"
				PaperProps={{
					sx: {
						backgroundColor: theme.palette.background.paper,
						width: sidebarWidth,
						display: { xs: "none", lg: "block" },
					},
				}}
				variant="persistent"
			>
				{content}
			</Drawer>
		</>
	);
};

DashboardSidebar.propTypes = {
	onMobileClose: PropTypes.func,
	openMobile: PropTypes.bool,
};

export default DashboardSidebar;
